import { Events } from '../events/app-events';
import { requires } from '../mixins/dependency';
import { defer } from '../utils/async-store';
import { eventBus } from '../utils/event-bus';
import { persistance } from '../utils/persistance';
import { ApiError, ApiService } from './api-service';
import { BaseService } from './base-service';
import { QuestionsService } from './questions-service';
import { ConfigService } from './config-service';
import { Question } from '../model/question';
import { t } from '../utils/locale';
import { State, WebSectionTrackingData } from '@FLAVOR/../../common/types';
import { getV2States } from '@ahoy/model/dist/utils/states';
import { LandingPageService } from '@APP/services/landing-page-service';
import { DeviceType, SubscriptionData } from '@ahoy/types';
import { UserType } from '@ahoy/model/dist/types';
import { getQueryParam } from '@ahoy/aquarium/dist/utils/getQueryParam';

type Deps = {
  apiService: ApiService;
  questionsService: QuestionsService;
  configService: ConfigService;
  landingPageService: LandingPageService;
};
export type LogoutOrigin = 'Back button' | 'Logout button (agents)' | 'Exit popup';
export type LogoutData = { reload: boolean; origin: LogoutOrigin; };
export type requestOtpCodeResponse = { status: 'error' | 'ok'; error?: string; qId?: string; };
export type LoginMode = 'login' | 'signup';
export namespace Result {
  export type OK = {
    timestamp: number,
    status: 'ok',
    data: any,
  };

  export type Error = {
    timestamp: number,
    status: 'error',
    error: any,
  };
}

type PaymentDetailsForRenewal = Pick<SubscriptionData, 'PaymentOption' | 'PaymentMethod' | 'CardNumber' | 'BankAccountField'>;
type UpdatedPaymentDetailsForRenewal = {type:'Bank Account' |'Credit Card' |'', last4:string}

@requires(ApiService.serviceName, QuestionsService.serviceName, ConfigService.serviceName, LandingPageService.serviceName)
export class UserService extends BaseService<Deps> {
  static serviceName = 'userService';

  store = persistance('ahoy-userinfo');

  private sectionProgress = persistance('lh-progress-notifications');

  public loginMode: LoginMode = 'signup';
  public userType: UserType = 'customer';
  public allowedToSellAgent: string[] = [];
  public allowedToSellAgency: string[] = [];
  public isLoggedIn: boolean = false;
  public completedWelcomeSection: boolean = false;
  public device_type: DeviceType = 'desktop';
  private stormStates: State[] = [];
  private insuredStateForMta: State | null = null;
  private programVersionForMta: string = '';
  private _sendRenewalQuoteBtnEnabled: boolean = true;
  private _updatePaymentMethodBtnEnabled: boolean = false;
  private ocrImageSrc: string = '';
 
  constructor() {
    super();
    this.store.acceptsCookies = this.store.acceptsCookies || false;

    eventBus().on(Events.Type.NavigatingOutOfNewLp, (data) => {
      this.handleCustomLandingPage(data);
    });
    eventBus().on<any>(Events.Type.Logout, (logoutData: LogoutData) => {
      eventBus().emit(Events.Type.LogExitOnBackgroundSend, { op: 'exit' });
      const { origin: logoutOrigin } = logoutData;
      this.deps.questionsService.logLogout(logoutOrigin);
      const { reload } = logoutData;
      this.logout(reload);
    });

    eventBus().on<Question>(Events.Type.QuestionUpdate, (q) => {
      if (['owner_name', 'owner_phone', 'email', 'watercraft_name'].includes(q.questionId)) {
        const { owner_name, owner_phone, email, watercraftName } = this.deps.questionsService.getQuestionsValues(['owner_name', 'owner_phone', 'email', 'watercraft_name']);
        this.deps.questionsService.saveOtpTracking('welcome-update', owner_phone, q.questionId, owner_name, email, watercraftName, this.store.userID, this.device_type);
        /** Send S_Welcome data before login to gtm */
        this.sendWebtrackingData('welcome-update', '');
      }
    });
  }

  public changeLoginMode(mode: LoginMode) {
    this.loginMode = mode;
    this.deps.questionsService.changeQueryParams('MODE', mode);
  }

  public getNameTitle(): string {
    const name = this.deps.questionsService.model.getQuestionById('owner_name')?.value as String || '';
    return name ? name.split(' ')[0] : '';
  }

  public getFullPhone(): string {
    const phone = this.getFullServerPhone();
    if (phone) {
      return phone.slice(0, 2) + phone.slice(3);
    }
    return '';
  }

  private getFullServerPhone(): string {
    return this.deps.questionsService.model.getQuestionById('owner_phone')?.value as string || this.store.phone;;
  }

  private routeToMyArea() {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const paramsStr = params.toString() ? `?${params.toString()}` : '';

    if (window.location.hostname.startsWith('localhost')) {
      eventBus().emit(Events.Type.GoToWebsite, { url: `http://localhost:8081${paramsStr}` });
    } else {
      eventBus().emit(Events.Type.GoToWebsite, { url: window.location.origin + `/my-area/index.html${paramsStr}` });
    }
  }

  public async routeToPortal() {
    const { isSSOExternalAgent, token } = await this.checkSSOexternalAgent();
    const isLocalhost = window.location.hostname.startsWith('localhost');
    const baseUrl = isLocalhost ? 'http://localhost:8002' : '';
    const portalUrl = isSSOExternalAgent ? `/portal/#/?token=${token}` : '/portal/';
    const finalUrl = baseUrl + portalUrl;
    eventBus().emit(Events.Type.GoToWebsite, { url: finalUrl });
  }

  private async checkSSOexternalAgent() {
    const result = await this.deps.apiService.http('/whoami/sso');
    return result;
  }

  async handleUserAcceptsCookies() {
    this.store.acceptsCookies = true;
    await this.autoLogin({});
    return defer('acceptsCookies').resolve(true);
  }

  private async handleCustomLandingPage(data: any) {
    this.saveOtpTrackingLoad(data);
    return defer('customLandingPage').resolve(true);
  }

  public getUserType() {
    return this.userType;
  }

  private setStormStates(stormStates: State[]) {
    this.stormStates = stormStates;
    eventBus().emit(Events.Type.SetStormStates, { stormStates });
  }

  private setPaymentDetailsForRenewal(details: PaymentDetailsForRenewal) {
    this.paymentDetailsForRenewal = details;
    // alter question Renewal_Payment_Details text to show the payment details
    const renewalPaymentDetailsQuestion = this.deps.questionsService.model.getQuestionById('Renewal_Payment_Details');
    const userWantsToChangePaymentMethod = this.deps.questionsService.model.getQuestionById('Renewal_User_Override_Payment_Method_Change_YN')?.value === '$_BOOL_true';
    // if the user wants to change the payment method, set the Send_Renewal_Quote_btn_enabled flag to false
    if (userWantsToChangePaymentMethod) {
      this.forceUserToUpdatePaymentMethod();
    } else {
      this.allowUserToCompleteRenewal();
    }
    if (renewalPaymentDetailsQuestion) {
      const { PaymentOption, PaymentMethod, CardNumber, BankAccountField } = details;
      const paymentOptionFormatted = PaymentOption === 'Annual' ? 'Pay in Full' : (PaymentOption === 'Monthly'? 'Installments' :'None');
      const paymentMethodFormatted = PaymentMethod === 'ACH' ? 'Bank Account' : (PaymentMethod === 'Credit_Card' ? 'Credit Card' : 'None');
      // if the payment option is None, set the Send_Renewal_Quote_btn_enabled flag to false
      if (!PaymentMethod || paymentMethodFormatted === 'None') {
        this.forceUserToUpdatePaymentMethod();
      }
      const isCreditCard = PaymentMethod === 'Credit_Card';
      const isBankAccount = PaymentMethod === 'ACH';
      const cardNumber = CardNumber || '';
      const bankAccount = BankAccountField || '';
      const paymentMethodText = '<b>Payment method:</b> ' + paymentMethodFormatted + (CardNumber && isCreditCard ? ` ${cardNumber}` : '') + (BankAccountField && isBankAccount ? ` ${bankAccount}` : '');
      this.paymentDetailsForRenewalDefaultText = paymentMethodText;
      const paymentOptionText = '<b>Payment option:</b> ' + paymentOptionFormatted;
      renewalPaymentDetailsQuestion.text = `<div>${paymentOptionText}</div><div>${paymentMethodText}</div>`;
    }
  }
  
  public setUpdatedPaymentDetailsForRenewal({ details = null, displayUpdatePaymentMethodText = false }: { details: UpdatedPaymentDetailsForRenewal|null,displayUpdatePaymentMethodText?: boolean }) {
    const renewalUpdatedPaymentDetailsQuestion = this.deps.questionsService.model.getQuestionById('Renewal_Payment_Details_Updated');
    // this means the user has changed the payment method
    if (renewalUpdatedPaymentDetailsQuestion) {
      // this is called when the user must update the payment method, but the user hasn't changed it yet
      // the text should be displayed in red
      if (displayUpdatePaymentMethodText) {
        const text = 'Please update your payment method.';
        renewalUpdatedPaymentDetailsQuestion.text = `<div style="color:red">${text}</div>`;
      }
      else if (details) {
        this.updatedPaymentDetailsForRenewal = details;
        // alter question Renewal_Payment_Details_Updated text to show the payment details
        const { type, last4 } = details;
        // notify questions service that the payment method has been updated
        this.deps.questionsService.setPaymentMethodUpdatedInRenewal({ type, last4 });
        // const updatedPaymentOption = this.deps.questionsService.paymentOption;
        // const updatedPaymentOptionFormatted = updatedPaymentOption === 'Annual' ? 'Pay in Full' : (updatedPaymentOption === 'Monthly' ? 'Installments' : 'None');
        // const updatedPaymentMethodText = type && last4 ? '<b>Payment method:</b> ' + type + ' ' + last4 : 'None';
        let updatedPaymentMethodText = type && last4 ? type + ' ' + last4 : 'None';
        // const updatedPaymentOptionText = '<b>Payment option:</b> ' + updatedPaymentOptionFormatted;
        // this means the user has changed the payment method. if not, we display the default payment method
        this.updatedPaymentDetailsForRenewalDefaultText = updatedPaymentMethodText;
        // renewalUpdatedPaymentDetailsQuestion.text = `<div>${updatedPaymentOptionText}</div><div>${updatedPaymentMethodText}</div>`;
        // remove <b>Payment method:</b> from the text
        updatedPaymentMethodText = updatedPaymentMethodText.replace('<b>Payment method:</b>', '');
        renewalUpdatedPaymentDetailsQuestion.text = `<div>${updatedPaymentMethodText}</div>`;
      }
      // the user hasn't changed the payment method, we still need to show the payment option
      else {
      // reset the updated payment details to the default payment details
      // this.deps.questionsService.setPaymentMethodUpdatedInRenewal({ type: '', last4: '' });
      // const updatedPaymentOption = this.deps.questionsService.paymentOption;
      // const updatedPaymentOptionFormatted = updatedPaymentOption === 'Annual' ? 'Pay in Full' : (updatedPaymentOption === 'Monthly' ? 'Installments' : 'None');
      // const updatedPaymentOptionText = '<b>Payment option:</b> ' + updatedPaymentOptionFormatted;
        let updatedPaymentMethodText = this.updatedPaymentDetailsForRenewalDefaultText?`<div>${this.updatedPaymentDetailsForRenewalDefaultText}</div>`:`<div>${this.paymentDetailsForRenewalDefaultText}</div>`;
        // renewalUpdatedPaymentDetailsQuestion.text = `<div>${updatedPaymentOptionText}</div><div>${updatedPaymentMethodText}</div>`;
        // remove <b>Payment method:</b> from the text
        updatedPaymentMethodText = updatedPaymentMethodText.replace('<b>Payment method:</b>', '');
        renewalUpdatedPaymentDetailsQuestion.text = `<div>${updatedPaymentMethodText}</div>`;
      }
    }  
  }

  private setChargeProcessingFee(chargeProcessingFee: boolean) {
    this.deps.questionsService.setChargeProcessingFee(chargeProcessingFee);
  }

  public forceUserToUpdatePaymentMethod() {
    this.sendRenewalQuoteBtnEnabled = false;
    this.updatePaymentMethodBtnEnabled = true;
    this.setUpdatedPaymentDetailsForRenewal({ details: null, displayUpdatePaymentMethodText: true });
  }

  public allowUserToCompleteRenewal() {
    this.sendRenewalQuoteBtnEnabled = true;
    this.updatePaymentMethodBtnEnabled = false;
  }
  
  public allowUserToCompleteRenewalOrUpdateAgain() {
    this.sendRenewalQuoteBtnEnabled = true;
    this.updatePaymentMethodBtnEnabled = true;
  }
 
  set sendRenewalQuoteBtnEnabled(value: boolean) {
    this._sendRenewalQuoteBtnEnabled = value;
  }
  
  public get sendRenewalQuoteBtnEnabled() {
    return this._sendRenewalQuoteBtnEnabled;
  }
 
  set updatePaymentMethodBtnEnabled(value: boolean) {
    this._updatePaymentMethodBtnEnabled = value;
  }
  
  public get updatePaymentMethodBtnEnabled() {
    return this._updatePaymentMethodBtnEnabled;
  }

  private setAffiliate({ data, fromAutoLogin = false, fromGetQuestionaire = false }: any) {
    if (!data || (!fromAutoLogin && !fromGetQuestionaire)) return;
    let affiliateValue = null;
    if (fromAutoLogin || fromGetQuestionaire) { affiliateValue = data.questionnaire?.questions?.find((q: any) => q.questionId === 'Affiliate')?.value; }
    if (affiliateValue) this.deps.questionsService.affiliateService?.setAffiliate(affiliateValue);
  }
 
  private setDataForMta(data:  {insuredStateForMta:State, programVersionForMta:string}) {
    this.insuredStateForMta = data?.insuredStateForMta;
    this.programVersionForMta = data?.programVersionForMta;
  }
  
  public getDataForMta() {
    return { insuredStateForMta: this.insuredStateForMta, programVersionForMta: this.programVersionForMta };
  }
  
  public getStormStates() {
    return this.stormStates;
  }

  public setOcrImageSrc(src: string) {
    this.ocrImageSrc = src;
  }
  
  public getOcrImageSrc() {
    return this.ocrImageSrc;
  }

  public get isDev() {
    const watercraft_name = this.deps.questionsService.model.getQuestionById('watercraft_name')?.value;
    const watercraft_name_string = typeof watercraft_name === 'string' ? watercraft_name : '';
    return (process.env.NODE_ENV && ['development','local'].includes(process.env.NODE_ENV)) || (watercraft_name_string && ['ahoy-testing-dev','ahoy-dev'].includes(watercraft_name_string));
  }

  public get isStaging() {
    return (process.env.NODE_ENV && ['staging','development','local'].includes(process.env.NODE_ENV)) || this.deps.questionsService.model.getQuestionById('watercraft_name')?.value === 'ahoy-testing-staging';
  }
  
  public get isTest() {
    return this.deps.questionsService.model.getQuestionById('IsTest')?.value === '$_BOOL_true';
  }  
  
  public setProgramVersion = ({ calculatedState='',effectiveDate='' }: {calculatedState?:string,effectiveDate?:string}) => {
    const createValidDate = (paramToCheck:any) => {
      const date = new Date(paramToCheck);
      // Check if the date is invalid by seeing if getTime() is NaN
      if (isNaN(date.getTime())) {
        // If the date is invalid, return the current date/time
        return new Date();
      }
      // If the date is valid, return it
      return date;
    };
    
    const model = this.deps.questionsService.model;
    if (!model) return;
    const state = calculatedState || model.getCheckByState();
    const programVersionQuestion = model.getQuestionById('Program_Version');
    const effectiveDateQuestion = model.getQuestionById('insurance_start_date');
    if (!programVersionQuestion || !state || !effectiveDateQuestion) return;
    const V2States:string[] = getV2States();
    effectiveDate = effectiveDate || effectiveDateQuestion.value as string;
    const dateForProgramVersion = effectiveDate? createValidDate(effectiveDate) : new Date();
    const dateIsAfterApr1st2024 = dateForProgramVersion >= new Date('2024-04-01');
    const dateIsAfterMay1st2024 = dateForProgramVersion >= new Date('2024-05-01');
    const isDev = (process.env.NODE_ENV && ['development','local'].includes(process.env.NODE_ENV)) || model.getQuestionById('watercraft_name')?.value === 'ahoy-testing-version';
    const isMta = this.deps.questionsService.isMta();
    // keep the program version on MTA
    if (isMta) {
      // on V1 MTA, keep the program version if the state remains the same as the insured state on the policy
      const { insuredStateForMta, programVersionForMta } = this.getDataForMta();
      const stateWasNotChangedDuringMTA = insuredStateForMta && insuredStateForMta === state;
      if (stateWasNotChangedDuringMTA) {
        programVersionQuestion.value = programVersionForMta;
        if (programVersionQuestion.value === '') programVersionQuestion.value = 'V1';
        return;
        // if not, we continue with the same logic as new app
      } 
      // if the program version is empty, set it to V1
      if (programVersionQuestion.value === '') programVersionQuestion.value = 'V1';
    };
    // set the program version based on the state and the insurance start date
    if (isDev) {
      if (dateIsAfterApr1st2024 && V2States.includes(state)) programVersionQuestion.value = 'V2';
      else programVersionQuestion.value = 'V1';
    }
    else {
      if (dateIsAfterMay1st2024 && V2States.includes(state)) programVersionQuestion.value = 'V2';
      else programVersionQuestion.value = 'V1';
    }
  };

  private async checkDisputesAfterLoadQuestionnaire(questionnaireId?: string) {
    const { questionsService } = this.deps;
    if (!questionnaireId) return;
    try {
      const response = await this.deps.apiService.http('/insure/request-offer/check-dispute', { questionnaireId });
      if (response && response.status === 'ok' && response.data && typeof response.data === 'object') {
        questionsService.setDisputeIssuesFound(true);
        questionsService.setDisputeIssuesData(response.data);
      } else {
        questionsService.setDisputeIssuesFound(false);
        questionsService.setDisputeIssuesData(null);
      }
    }
    catch  {
      return;
    }
  }

  private doPostLoadQuestionnaireActions({ data }: { data: any; }) {
    this.setProgramVersion({});
    data.paymentDetailsForRenewal && this.setPaymentDetailsForRenewal(data.paymentDetailsForRenewal);
    this.setUpdatedPaymentDetailsForRenewal({ details: data.updatedPaymentMethodForRenewal });
    this.setChargeProcessingFee(data.chargeProcessingFee);
    this.checkDisputesAfterLoadQuestionnaire(data.questionnaire.questionnaireId);
  }

  public async autoLogin({ isLandingPage = false, isMobile = false }) {
    try {
      // we don't want to lock the api service if we are on the landing page to avoid displaying the loader
      if (!isLandingPage) this.deps.apiService.lock();
      const result = await this.deps.apiService.http('/hello' + window.location.search, { isMobile, isPostCall: false });
      if (!result.data) throw new Error('No data returned');
      const { data } = result;
      if (data.not_allowed_to_use_Agents_Portal) {
        this.deps.questionsService.setNot_allowed_to_use_Agents_Portal(data.not_allowed_to_use_Agents_Portal);
        // this.routeToPortal()
        this.deps.questionsService.showNot_allowed_to_use_Agents_PortalPopup()
        return
      }
      // debugger;
      if (data.stubMyAhoyToken) {
        this.routeToMyArea();
      }
      this.setStormStates(data.stormStates);
      this.setAffiliate({ data:data, fromAutoLogin:true });
      if (data.insuredStateForMta && data.programVersionForMta) this.setDataForMta(data);
      if (data.ocrImageSrc) this.setOcrImageSrc(data.ocrImageSrc);;
      this.deps.apiService.setToken(data.token);
      if (data.designedLp) {
        eventBus().emit(Events.Type.CustomLpLoad, data.designedLp);
      }
      // if we aren't on the landing page we want to display the funnel
      else {
        eventBus().emit(Events.Type.RemoveFunnelHiding);
      }
      if (data.userType) {
        this.userType = data.userType;
        let myWindow = window as any;
        myWindow['userType'] = this.userType;
        // debugger
        this.deps.questionsService.setUserType(data.userType);
      }
      if (data.allowedToSellAgent) {
        this.deps.questionsService.setAgentStates(data.allowedToSellAgent);
        this.allowedToSellAgent = data.allowedToSellAgent;
      }
      if (data.allowedToSellAgency) {
        this.deps.questionsService.setAgencyStates(data.allowedToSellAgency);
        this.allowedToSellAgency = data.allowedToSellAgency;
      }
      if (data.ahoyLH.congrats) {
        const emarketmed_id_value = data?.questionnaire?.questions?.find((q:any)=>q.questionId === 'emarketmed_id')?.value || 0;
        this.handleEmarketmedId({ emarketmed_id_value });
        const totalPremium = data?.questionnaire?.questions?.find((q:any)=>q.questionId === 'Total_Premium')?.value || 0;
        const gtmPayload = { revenue:totalPremium, taboolaAlreadyReportedOnCheckout:true };
        this.isLoggedIn = true;
        eventBus().emit(Events.Type.Refresh, this.deps.questionsService.model.getStatus());
        eventBus().emit(Events.Type.Congratulations, { congrats: true, paidNowSuccessfully:true, gtmPayload });
      }
      else if (data.ahoyLH.customer) {
        this.routeToMyArea();
      }
      else if (data.questionnaire) {
        let localOverrides: any = {};

        if (!data.questionnaire.questionnaireId) {
          if (data.userType == 'customer') {
            localOverrides = {
              Q_provide_name: this.store.name,
              Q_provide_phone: this.store.phone,
              Q_Email: this.store.email,
              Q_provide_boat_name: this.store.watercraft_name,
              Custom_Landing_Page: data.designedLp || '$_BOOL_false',
            };
          }
        } else if (data.ahoyLH && data.ahoyLH.phone) {
          const { number, country } = data.ahoyLH.phone;
          if (number && country) {
            localOverrides['Q_provide_phone'] = `${country} ${number}`;
          }
        }

        this.deps.questionsService.syncFromServer(
          data.questionnaire,
          localOverrides,
        );
        if (data.questionnaire.questionnaireId) {
          this.deps.questionsService.model.lockPhone(true);
        }
        this.handleEmarketmedId();
        this.isLoggedIn = true;
        eventBus().emit(Events.Type.Refresh, this.deps.questionsService.model.getStatus());
        this.doPostLoadQuestionnaireActions({ data });
      }

    } catch (err) {
      this.isLoggedIn = false;
      if (err instanceof ApiError) {
        /** Unauthenticated questionnaire returned */
        if (err.data.designedLp) {
          eventBus().emit(Events.Type.CustomLpLoad, err.data.designedLp);
        }
        // if we aren't on the landing page we want to display the funnel
        else {
          eventBus().emit(Events.Type.RemoveFunnelHiding);        
        }
        const { questionnaire } = err.data;
        if (questionnaire) {
          this.deps.questionsService.syncFromServer(questionnaire, {
            Q_provide_name: this.store.name,
            Q_provide_phone: this.store.phone,
            Q_Email: this.store.email,
            Q_provide_boat_name: this.store.watercraft_name,
            Custom_Landing_Page: err.data.designedLp || '$_BOOL_false',
          });
        }
      }
    } finally {
      this.deps.apiService.unlock();
    }
  }

  public getTextParamsForLandingPage() { 
    const landing_page_title = getQueryParam('landing_page_title', true);
    const landing_page_subtitle = getQueryParam('landing_page_subtitle', true);
    const text_max_width_column = getQueryParam('text_max_width_column', true );
    return { landing_page_title, landing_page_subtitle, text_max_width_column };
  }

  private getLogRocketSessionURL() { 
    const metaTag = document.querySelector('meta[name="env"]');
    if (metaTag) {
      // get the 'data-logrocket-session-url' attribute value
      return metaTag.getAttribute('data-logrocket-session-url');
    } 
    return '';
  }

  private handleEmarketmedId({ emarketmed_id_value } = { emarketmed_id_value: '' }) {
    try {
      const emarketmed_id = emarketmed_id_value || this?.deps?.questionsService?.model?.getQuestionById('emarketmed_id')?.value ||  '';
      if (emarketmed_id && typeof emarketmed_id === 'string' && emarketmed_id.length > 0) {
        this.deps.questionsService.setEmarketmedId(emarketmed_id);
      }
    } catch  {
      return;
    }
  }

  public async getQuestionaire({ createIfNotExist, isMobile }: { createIfNotExist: boolean, isMobile: boolean; }) {
    const { owner_name: name, email, watercraft_name, Custom_Landing_Page } = this.deps.questionsService.getQuestionsValues(['owner_name', 'email', 'watercraft_name', 'Custom_Landing_Page']);
    const { otpToken } = this.store;
    const externalAgentQueryParam = getQueryParam('externalAgent');
    const questionnaireId = getQueryParam('questionnaireId');
    const LogRocketSessionURL = this.getLogRocketSessionURL();
    const externalAgent = createIfNotExist && externalAgentQueryParam ? externalAgentQueryParam : undefined;
    try {
      this.deps.apiService.lock();
      const data = await this.deps.apiService.http('/hello' + window.location.search, {
        isMobile,
        createIfNotExist,
        name,
        phone: this.getFullServerPhone(),
        email,
        LogRocketSessionURL,
        watercraft_name,
        Custom_Landing_Page,
        otpToken,
        externalAgent,
        externalAgentParam: externalAgentQueryParam,
        URL: window.location.search,
        questionnaireId,
      });
      if (data.questionnaire?.questionnaireStatus != 'New') { this.sendWebtrackingData('Login', ''); }
      this.deps.apiService.setToken(data.token);
      this.setStormStates(data.stormStates);
      if (data.ocrImageSrc) this.setOcrImageSrc(data.ocrImageSrc);
      this.setAffiliate({ data, fromGetQuestionaire:true });
      if (data.ahoyLH.customer) {
        this.routeToMyArea();
      } else if (data.questionnaire) {
        let localOverrides: any = {};
        if (!data.questionnaire.questionnaireId) {
          localOverrides = {
            Q_provide_name: this.store.name,
            Q_provide_phone: this.store.phone,
            Q_Email: this.store.email,
            Q_provide_boat_name: this.store.watercraft_name,
            Custom_Landing_Page: data.designedLp || '$_BOOL_false',
          };
        } else if (data.ahoyLH) {
          const { number, country } = data.ahoyLH.phone;
          if (number && country) {
            localOverrides['Q_provide_phone'] = `${country} ${number}`;
          }
        }
        this.deps.questionsService.syncFromServer(
          data.questionnaire,
          localOverrides,
        );
        this.deps.questionsService.model.lockPhone(data.questionnaire.questionnaireId);
        this.handleEmarketmedId();
        this.isLoggedIn = true;
        eventBus().emit(Events.Type.LoginSuccess, { 'email': this.deps.questionsService.model.getQuestionById('email')?.value });
        this.doPostLoadQuestionnaireActions({ data });
      }
    } finally {
      this.deps.apiService.unlock();
    }
  }

  public async reStart() {
    if (this.userType == 'customer') {
      this.deps.questionsService.requestSectionChange('S_Boat');
    } else {
      const agentLogin = this.deps.configService.get('lighthouseApiEndpoint') as string + '/marina/agent';
      eventBus().emit(Events.Type.GoToWebsite, { url: agentLogin });
    }
  }

  async logout(reload: boolean = true) {
    this.isLoggedIn = false;
    Object.keys(this.store).forEach((key) => {
      if (key !== 'userID') {
        delete this.store[key];
      }
    });
    Object.keys(this.sectionProgress).forEach(
      (key) => delete this.sectionProgress[key],
    );
    if (this.deps.questionsService.isExternalAgent()) {
      const auth0_domain = 'https://' + this.deps.configService.get('auth0Domain');
      const auth0_client_id = this.deps.configService.get('auth0ClientId') || '';
      const portal = (window.location.hostname.startsWith('localhost')) ? 'http://localhost:8002/' : window.location.origin + '/portal/';
      const logout = new URL('v2/logout', auth0_domain);
      logout.searchParams.append('returnTo', portal);
      logout.searchParams.append('client_id', auth0_client_id);

      this.deps.apiService
        .http('/goodbye')
        .finally(() => eventBus().emit(Events.Type.GoToWebsite, { url: logout.toString() }));
    } else if (reload) {
      this.deps.apiService
        .http('/goodbye')
        .finally(() => {
          // this is to solve an issue when a user completes and app, gets to congrats screen, logs out and then logs in again, 
          // but the questionnaire is still in congrats screen because the questionnaireId is in the url
          const getModifiedUrl = (currentUrl: string) => {
            // Create a URL object from the current URL
            const url = new URL(currentUrl);
            // Access the query string parameters
            const params = new URLSearchParams(url.search);
            // Remove the questionnaireId parameter
            params.delete('questionnaireId');
            params.delete('congrats');
            // Drop the AI MTA route param too — otherwise main-view's AI MTA bypass
            // re-flags the user as logged in and renders the screen with a missing
            // #policyId hash (the hash is stripped in the rebuilt URL below), which
            // surfaces as a "Missing policy id" error after a logout from AI MTA.
            params.delete('additionalInsuredMta');
            params.set('MODE', 'login');
            // Construct the new URL without the questionnaireId parameter
            const newUrl = `${url.origin}${url.pathname}?${params.toString()}`;
            // return to the new URL, without the qId parameter
            return newUrl;
          };
          
          eventBus().emit(Events.Type.GoToWebsite, { url: getModifiedUrl(window.location.href) });
        });
    } else {
      await this.deps.apiService.http('/goodbye');
      // alert: true prompts the user to confirm they want to leave the page.
      // on paymentLinkMode we don't want to prompt the user 
      const isPaymentLinkMode = this.deps.questionsService.isPaymentMode();
      eventBus().emit(Events.Type.GoToWebsite, { alert: isPaymentLinkMode ? false : true });
    }
  }

  public async requestOtpCode(validateEmail: boolean, loggedIn = false): Promise<requestOtpCodeResponse> {
    const { email, watercraft_name } = this.deps.questionsService.getQuestionsValues(['email','watercraft_name']);
    const phone = this.getFullPhone();
    if (phone) {

      try {
        this.deps.apiService.lock();
        const res: Result.Error | Result.OK = await this.deps.apiService.http('/hello/otp', { phone, email, validateEmail, loggedIn, override: !!watercraft_name?.includes('ahoy-testing') });
        if (res.status === 'error') {
          this.saveOtpTracking('send-otp', '', t(res.error.status));
          /** Send S_Welcome data before login to gtm */
          this.sendWebtrackingData('send-otp', t(res.error.status));
          return { status: 'error', error: res.error.status, qId: res.error.qId };
        }
        else {
          this.saveOtpTracking('send-otp', '', '');
          /** Send S_Welcome data before login to gtm */
          this.sendWebtrackingData('send-otp', '');
          return { status: 'ok' };
        }
      } finally {
        this.deps.apiService.unlock();
      }
    }
    return { status: 'error', error: 'no phone' };
  }

  private switchNumber(phoneNumber: any) {
    // Insert Israeli Phone number with '11' instead of leading '05'
    // '+11107989230'=>'+972507989230'
    return '+9725' + phoneNumber.slice(4);
  }

  public setCompletedWelcomeSection() {
    this.completedWelcomeSection = true;
  }

  public async submitOtpCode({ code, isMobile }:{ code: string, isMobile: boolean }) {
    let phone = this.getFullPhone();
    if (phone.startsWith('+111')) {
      phone = this.switchNumber(phone);
    }

    if (code && phone) {
      try {
        this.deps.apiService.lock();
        const result = await this.deps.apiService.http('/hello/otp/try', { phone, code });
        if (result.status === 'ok') {
          this.saveOtpTracking('send-code', code, '');
          /** Send S_Welcome data before login to gtm */
          this.sendWebtrackingData('send-code', '');
          this.getQuestionaire({ createIfNotExist: this.loginMode == 'signup', isMobile });
        } else {
          this.saveOtpTracking('send-code', code, 'error');
          /** Send S_Welcome data before login to gtm */
          this.sendWebtrackingData('send-code', 'error');
          throw new Error('OTP Failed');
        }
      } catch  {
        this.saveOtpTracking('send-code', code, 'OTP failed');
        /** Send S_Welcome data before login to gtm */
        this.sendWebtrackingData('send-code', 'OTP failed');
        throw new Error('OTP Failed');
      } finally {
        this.deps.apiService.unlock();
      }
    }
  }

  private generateQuickGuid() {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  private setUserIdIfEmpty() {
    if (!this.store.userID) {
      const userID = this.generateQuickGuid();
      this.store.userID = userID;
    }
  }
  public async saveOtpTracking(operation: string, code: string, errorMessage: string) {
    this.setUserIdIfEmpty();
    const { owner_name, email } = this.deps.questionsService.getQuestionsValues(['owner_name', 'email']);
    this.deps.questionsService.saveOtpTracking(operation, this.getFullPhone(), code, owner_name || this.store.name, email || this.store.email, errorMessage, this.store.userID, this.device_type);

  }

  public async saveOtpTrackingLoad(data?: any) {
    this.setUserIdIfEmpty();
    if (data && data?.operation) this.deps.questionsService.saveOtpTracking(data?.operation, this.getFullPhone(), '', this.store.name, this.store.email, this.store.watercraft_name, this.store.userID, this.device_type);
    else this.deps.questionsService.saveOtpTracking('load', this.getFullPhone(), '', this.store.name, this.store.email, this.store.watercraft_name, this.store.userID, this.device_type);
  }

  public prepareDataForCustomLpEvent() {
    const { name, email, watercraft_name, userID } = this.store;
    let data;
    data = {
      phone: this.getFullPhone(),
      name,
      email,
      watercraft_name,
      userID,
      deviceType: this.device_type,
      next_section: 'S_Welcome',
      section: 'S_Welcome_Custom_Lp',
      operation: 'next',
      errorMessage: '',
    };
    return data;
  }

  /** Sending tracking data to gtm  */
  public async sendWebtrackingData(operation: string, errorMessage: string) {
    let data: WebSectionTrackingData;
    data = await this.prepareTrackingData(operation, errorMessage);
    eventBus().emit(Events.Type.SaveWebSectionTracking, data);
  };

  private async prepareTrackingData(operation: string, errorMessage: string): Promise<WebSectionTrackingData> {
    let data;
    const { model } = this.deps.questionsService;
    const noNextSection = ['Arrival', 'load', 'send-otp', 'send-code', 'welcome-next', 'welcome-update', 'welcome-error', 'about-popup', 'change-to-signup', 'change-to-login'].includes(operation);
    const nextSection = noNextSection ? '' : model.getSectionAfter(model.getActiveSectionName());

    data = {
      section: 'S_Welcome',
      next_section: nextSection || '',
      operation: operation,
      errorMessage: errorMessage || '',
    };

    return data;
  }
}

UserService.define();
